import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Chatbot from "./Chatbot";
import ChatbotSettingsPanel from "./ChatbotSettingsPanel";
import { getChatbotConfig, updateChatbotConfig } from "../apis/chatbots";

export default function ChatbotWithSettings({ onNavigateBack }) {
  const { instanceId, chatbotId: routeChatbotId } = useParams();
  const navigate = useNavigate();

  const userId = localStorage.getItem("user_id");
  const chatbotId = routeChatbotId || "new";
  const [mode, setMode] = useState(chatbotId === "new" ? "create" : "edit");
  const [flowConfig, setFlowConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getChatbotConfig(chatbotId);
        if (!data) throw new Error("Not found");
        if (!isMounted) return;
        setFlowConfig({
          autoMessage: data.initial_message || "",
          botName: data.bot_name || "Unnamed Bot",
          responses: data.responses || [],
          startTriggers:
            typeof data.startTriggers === "string"
              ? JSON.parse(data.startTriggers)
              : Array.isArray(data.startTriggers)
              ? data.startTriggers
              : ["start"],
        });
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to load chatbot config");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (mode === "edit" && chatbotId !== "new") {
      fetchConfig();
    } else if (mode === "create") {
      setFlowConfig({
        autoMessage: "Hello! How can I help you today?",
        botName: "My New Chatbot",
        responses: [],
        startTriggers: ["start"],
      });
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [chatbotId, mode]);

  const updateFlow = (updatedFlow) =>
    setFlowConfig((prev) => ({ ...prev, ...updatedFlow }));

  const saveChanges = async (autoMsg, responses, botName, startTriggers) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const trimmedResponses = (responses || [])
        .map((r) => ({
          trigger: (r.trigger || "").trim(),
          action: r.action || "message",
          content: (r.content || "").trim(),
        }))
        .filter((r) => r.trigger.length > 0);

      const processedStartTriggers = (startTriggers || ["start"])
        .map((trigger) => trigger.trim().toLowerCase())
        .filter((trigger) => trigger.length > 0);

      if (processedStartTriggers.length === 0) processedStartTriggers.push("start");

      let payload = {
        bot_name: botName.trim(),
        initial_message: autoMsg.trim(),
        responses: trimmedResponses,
        start_triggers: processedStartTriggers,
      };

      if (mode === "create") {
        if (!instanceId || !userId)
          throw new Error("instanceId and userId are required to create a chatbot");
        payload.instance_id = instanceId;
        payload.user_id = userId;
      }

      const endpointId = mode === "create" ? "new" : chatbotId;
      const res = await updateChatbotConfig(endpointId, payload);

      if (mode === "create") {
        const newId = res.id || res.chatbotId;
        if (!newId) throw new Error("Server didn't return new chatbot ID");
        setMode("edit");
        setSuccess("Chatbot created successfully!");
        navigate(`/instance/${instanceId}/chatbots`);
      } else {
        setSuccess("Chatbot updated successfully!");
      }

      updateFlow({
        autoMessage: autoMsg.trim(),
        responses: trimmedResponses,
        botName: botName.trim(),
        startTriggers: processedStartTriggers,
      });
    } catch (err) {
      setError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#25d366] mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <p className="text-lg text-red-600 font-semibold mb-2">Error</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!flowConfig) return null;

  return (
    <div 
      style={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: '#f0f2f5'
      }}
    >
      {/* LEFT PANEL - CHATBOT */}
      <div 
        style={{
          flex: '3 1 0%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRight: '1px solid #e5e7eb'
        }}
      >
        {/* Chat Header - Fixed */}
        <div 
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: '#f0f2f5',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}
        >
          <button
            onClick={() => (onNavigateBack ? onNavigateBack() : navigate(-1))}
            className="text-gray-600 hover:text-[#25d366] transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#25d366] rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {flowConfig.botName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="font-semibold text-gray-800 truncate">{flowConfig.botName}</h2>
              <p className="text-sm text-gray-500">Online</p>
            </div>
          </div>
        </div>

        {/* Chat Content - Scrollable */}
        <div 
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Chatbot flowConfig={flowConfig} />
        </div>
      </div>

      {/* RIGHT PANEL - SETTINGS */}
      <div 
        style={{
          flex: '2 1 0%',
          minWidth: '300px',
          maxWidth: '400px',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff'
        }}
      >
        {/* Settings Header - Fixed */}
        <div 
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: '#f0f2f5',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0
          }}
        >
          <h2 className="text-lg font-semibold text-gray-800">Chatbot Settings</h2>
          <p className="text-sm text-gray-500">Configure your bot behavior</p>
        </div>

        {/* Settings Content - Scrollable */}
        <div 
  style={{
    flex: '1 1 0%',
    minHeight: 0,
    overflowY: 'auto',
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // Internet Explorer and Edge
  }}
  className="scrollbar-hide" // Custom class for Webkit browsers
>
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3 }}
  >
    <ChatbotSettingsPanel
      flowConfig={flowConfig}
      onUpdate={updateFlow}
      onSave={saveChanges}
      saving={saving}
      success={success}
      error={error}
      mode={mode}
    />
  </motion.div>
</div>

      </div>
    </div>
  );
}
